"use client";

import { useState, useEffect } from "react";
import { ServiceCard } from "@/app/Components/Element_Components/serviceCard/ServiceCard";
import LinkWithArrow from "../../Element_Components/linkWithArrow/linkWithArrow";
import { useTranslate } from "@/utils/languageTranslation";
import { useLanguage } from "@/app/LanguageContext";
import { getBaseUrl } from "@/lib/getBaseURL";

interface Service {
  documentId: number;
  title: string;
  overview: string;
  shortDescription: string;
  type: string;
}

export const ServicesSection = () => {
  const {language} = useLanguage()
  const {t} = useTranslate()
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res1, res2] = await Promise.all([
          fetch(`${getBaseUrl()}/api/service-contents?populate=*&locale=${language}`),
          fetch(
            `${getBaseUrl()}/api/service-content-with-out-steps?populate=*&locale=${language}`
          ),
        ]);

        if (!res1.ok || !res2.ok) throw new Error("Failed to fetch services");

        const data1 = await res1.json();
        const data2 = await res2.json();

        const formattedServices: Service[] = [
          ...data1.data.map((item: any) => ({
            documentId: item.documentId,
            title: item.title,
            overview: item.overview,
            shortDescription: item.overview, // assuming `overview` is used as short description
            type: "step",
          })),
          ...data2.data.map((item: any) => ({
            documentId: item.documentId,
            title: item.title,
            overview: item.overview,
            shortDescription: item.overview,
            type: "text",
          })),
        ];

        setServices(formattedServices);
        setLoading(false);
      } catch (err: any) {
        setError("Error loading services");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <p className="text-center">Loading services...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <section className="py-12 px-6 bg-[#F4F8FB] mt-20">
      <div className="flex items-center justify-between max-w-[71rem] mx-auto py-5">
        <h2 className="text-2xl md:text-3xl font-bold text-[#002B4E] text-center">
          {t('service')}
        </h2>
        <LinkWithArrow href="/service" text={t('all_services')} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-[71rem] mx-auto">
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            title={service.title}
            description={service.shortDescription}
            className="w-full"
            href={`/service/serviceDetail/${service.documentId}/${service.type}`}
          />
        ))}
      </div>
    </section>
  );
};
